import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ESSAYS, TAGS } from "../data/mock";
import { Reveal } from "./Reveal";

const PAGE_SIZE = 6;

export const Essays = () => {
  const [visible, setVisible] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  // Infinite scroll: reveal the next batch when the sentinel enters view.
  useEffect(() => {
    if (visible >= ESSAYS.length) return;
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible((v) => Math.min(v + PAGE_SIZE, ESSAYS.length));
        }
      },
      { rootMargin: "300px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [visible]);

  const shown = ESSAYS.slice(0, visible);
  const hasMore = visible < ESSAYS.length;

  return (
    <section
      id="essays"
      data-testid="essays-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-20"
      style={{ background: "var(--brand-cream)" }}
    >
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <h2
              className="text-4xl md:text-6xl font-bold tracking-tight"
              style={{ color: "var(--brand-ink)" }}
              data-testid="essays-heading"
            >
              Essays
            </h2>
            <p
              className="max-w-sm text-base leading-relaxed"
              style={{ color: "var(--brand-muted)" }}
            >
              Slow-cooked notes on life, mindset, entrepreneurship,
              relationships, growth and evolution.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mb-10 flex flex-wrap gap-2" data-testid="essays-tag-cloud">
            {TAGS.map((t) => (
              <Link
                key={t.slug}
                to={`/tags/${t.slug}`}
                data-testid={`essays-tag-${t.slug}`}
                className="inline-flex items-center gap-1 text-[13px] font-semibold px-3.5 py-1.5 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  background: "var(--brand-cream-soft)",
                  border: "1px solid var(--brand-line)",
                  color: "var(--brand-ink-soft)",
                }}
              >
                #{t.label.toLowerCase()}
              </Link>
            ))}
          </div>
        </Reveal>

        <div className="divide-y" style={{ borderColor: "var(--brand-line)" }}>
          {shown.map((essay, i) => (
            <Reveal key={essay.id} delay={Math.min(i * 0.03, 0.18)}>
              <div
                className="group grid md:grid-cols-[auto_1fr_auto] items-start md:items-center gap-4 md:gap-10 py-8 md:py-10"
                style={{ borderTop: "1px solid var(--brand-line)" }}
              >
                <div
                  className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] md:min-w-[180px]"
                  style={{ color: "var(--brand-accent)" }}
                >
                  <span>{essay.category}</span>
                  <span className="w-1 h-1 rounded-full" style={{ background: "var(--brand-muted)" }} />
                  <span style={{ color: "var(--brand-muted)" }}>{essay.readTime}</span>
                </div>
                <div className="min-w-0">
                  <Link
                    to={`/essays/${essay.slug}`}
                    data-testid={`essay-card-${essay.id}`}
                    className="block"
                  >
                    <h3
                      className="text-2xl md:text-[30px] font-bold tracking-tight leading-[1.2] group-hover:opacity-80 transition-opacity"
                      style={{ color: "var(--brand-ink)" }}
                    >
                      {essay.title}
                    </h3>
                    <p
                      className="mt-3 text-[15px] md:text-base leading-relaxed line-clamp-2"
                      style={{ color: "var(--brand-muted)" }}
                    >
                      {essay.subtitle}
                    </p>
                  </Link>
                  {essay.tags?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {essay.tags.map((t) => {
                        const tag = TAGS.find((x) => x.slug === t);
                        if (!tag) return null;
                        return (
                          <Link
                            key={t}
                            to={`/tags/${t}`}
                            data-testid={`essay-${essay.id}-tag-${t}`}
                            className="text-[11px] font-semibold px-2.5 py-1 rounded-full hover:opacity-80"
                            style={{
                              background: "var(--brand-cream-soft)",
                              color: "var(--brand-muted)",
                            }}
                          >
                            #{tag.label.toLowerCase()}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Link
                  to={`/essays/${essay.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap"
                  style={{ color: "var(--brand-accent)" }}
                >
                  Read
                  <ArrowRight
                    size={15}
                    strokeWidth={2}
                    className="group-hover:translate-x-1 transition-transform duration-300"
                  />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        {hasMore && (
          <div
            ref={sentinelRef}
            data-testid="essays-load-more-sentinel"
            className="flex justify-center pt-14"
          >
            <button
              onClick={() => setVisible((v) => Math.min(v + PAGE_SIZE, ESSAYS.length))}
              data-testid="essays-load-more-button"
              className="inline-flex items-center gap-2 rounded-full btn-ghost font-semibold px-7 py-3.5"
            >
              Load more essays
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
